/* Copyright 2018 MathWorks, Inc */

define([
    "mw-utils/Utils",
    "mw-form/ContextMenu",
    "mw-form/MenuItem",
    "mw-form/MenuHeader",
    "mw-form/MenuSeparator"
], function (Utils, ContextMenu, MenuItem, MenuHeader, MenuSeparator) {

    var matchesSelector = function (/*Element*/ element, /*String*/ selector) { // TODO: use domUtils.matchesSelector instead
            var matchesMethod;

            if (!element || !selector) {
                return false;
            }

            if (element.msMatchesSelector) {
                matchesMethod = "msMatchesSelector";
            } else {
                matchesMethod = "matches";
            }

            return element[matchesMethod](selector);
        },
        VALID_PROPERTIES = {
            contributors: "Contributors",
            contextMenu: "ContextMenu"
        },
        _private = {
            _cmContributors: {},
            _contextMenus: {},

            _registerContextMenu: function (contextMenu) {
                if (contextMenu.get("managerId")) {
                    // No-op
                } else if (contextMenu.get("id")) {
                    contextMenu.managerId = contextMenu.get("id");
                } else {
                    contextMenu.managerId = Utils.generateUuid();
                }

                if (!_private._contextMenus.hasOwnProperty(contextMenu.managerId)) {
                    _private._contextMenus[contextMenu.managerId] = contextMenu;
                    _private._cmContributors[contextMenu.managerId] = {};
                }
                
                return contextMenu.managerId;
            },

            _unregisterContextMenu: function (contextMenu) {
                var managerId = _private._extractManagerId(contextMenu);

                if (_private._contextMenus.hasOwnProperty(managerId)) {
                    delete _private._contextMenus[managerId];
                    delete _private._cmContributors[managerId];
                }
            },
            
            _isRegistered: function (contextMenu) {
                var managerId = _private._extractManagerId(contextMenu);

                return ((managerId !== undefined) && _private._contextMenus.hasOwnProperty(managerId));
            },

            _extractManagerId: function (contextMenu) {
                var managerId;

                if ((contextMenu instanceof ContextMenu) && (contextMenu.managerId)) {
                    managerId = contextMenu.managerId;
                } else if (typeof contextMenu === "string") {
                    managerId = contextMenu;
                }

                return managerId;
            },
            
            _getContextMenu: function (contextMenu) {
                var managerId = _private._extractManagerId(contextMenu);
                
                if (_private._contextMenus.hasOwnProperty(managerId)) {
                    return _private._contextMenus[managerId];
                } else {
                    return null;
                }
            },

            _getContributors: function (contextMenu) {
                var managerId = _private._extractManagerId(contextMenu);

                if (_private._cmContributors.hasOwnProperty(managerId)) {
                    return Object.keys(_private._cmContributors[managerId]);
                } else {
                    return null;
                }
            },

            _validateProp: function (attr) {
                if (!VALID_PROPERTIES.hasOwnProperty(attr)) {
                    throw new Error("The input attribute '" + attr.toString() + "' is not valid;");
                }
            },

            _buildContextMenuContributors: function (contextMenu, targetNode) {
                if (!_private._isRegistered(contextMenu)) {
                    throw new Error("The context menu provided is not registered.");
                }
                
                var managerId = _private._extractManagerId(contextMenu),
                    selectors = Object.keys(_private._cmContributors[managerId]),
                    sectionTags = [],
                    sectionWidgets = {},
                    sectionItems = {},
                    cmItems = [],
                    processItem = function (item) {
                        var callback;
                        // TODO: Support all Menu Item types
                        switch (item.constructor) {
                            case (new MenuHeader()).constructor:
                            case (new MenuItem()).constructor:
                                break;
                            default:
                                // Support object descriptions, in case users don't want to include mw-form/MenuItem
                                if (typeof item === "object") {
                                    if (item.callback) {
                                        callback = item.callback;
                                    }
                                    item = new MenuItem(item);
                                    // TODO: Support touch device methods
                                    if (callback) {item.own(item.on("click", callback.bind(item)));}
                                } else {
                                    throw new Error("Expected input argument to be of type 'MenuItem' or 'Object'");
                                }
                        }

                        return item;
                    },
                    processSectionTag = function (sectionTag) {
                        if (sectionTag == null) { // Intentional use of '==', in order to check null & undefined
                            sectionTag = "default";
                        }

                        if ((typeof sectionTag !== "string")) {
                            throw new Error("Expected input argument to be of type 'MenuItem'");
                        }

                        if (sectionTags.indexOf(sectionTag) < 0) {
                            sectionTags.push(sectionTag);
                            sectionWidgets[sectionTag] = new MenuSeparator({tag: sectionTag}); // TODO: Support MenuHeaders as well
                            sectionWidgets[sectionTag].domNode.classList.add("mwMenuSection");
                            sectionItems[sectionTag] = [];
                            sectionWidgets[sectionTag].domNode.dataset.childCount = sectionItems[sectionTag].length;
                            cmItems.push(sectionWidgets[sectionTag]);
                        }

                        return sectionTag;
                    },
                    contextMenuBuilder = { // API used to customize the items in the toolstrip context menu
                        type: "ContextMenuBuilder",
                        version: "1.0.0",
                        addItems: function (items) {
                            if (!(items instanceof Array)) {
                                throw new Error("Expected input argument to be of type 'Array'");
                            }

                            items.forEach(function (item) {this.addItem(item, item.section)}.bind(this));
                        },
                        addItem: function (item, sectionTag) {
                            item = processItem(item);
                            sectionTag = processSectionTag(sectionTag);

                            if (cmItems.indexOf(item) > -1) {
                                this.placeItemIn(item, sectionTag);
                                return;
                            }

                            sectionItems[sectionTag].push(item);
                            sectionWidgets[sectionTag].domNode.dataset.childCount = sectionItems[sectionTag].length;
                            item.section = sectionTag;
                            var idx = cmItems.indexOf(sectionWidgets[sectionTag]) + sectionItems[sectionTag].length + 1;
                            cmItems.splice(idx, 0, item);
                        },
                        placeItemBefore: function (item, peer) { // Updates the section of the item
                            item = processItem(item);
                            peer = processItem(peer);

                            if (item.section !== peer.section) {
                                // Remove from sectionItems[item.section]
                                sectionItems[item.section].splice(sectionItems[item.section].indexOf(item), 1);
                                sectionWidgets[item.section].domNode.dataset.childCount = sectionItems[item.section].length;

                                // Place before peer in sectionItems[peer.section]
                                sectionItems[peer.section].splice(sectionItems[peer.section].indexOf(peer), 0, item);
                                sectionWidgets[peer.section].domNode.dataset.childCount = sectionItems[peer.section].length;

                                // Update section tag on item
                                item.section = peer.section;
                            }
                            cmItems.splice(cmItems.indexOf(item), 1);
                            cmItems.splice(cmItems.indexOf(peer), 0, item);
                        },
                        placeItemAfter: function (item, peer) {
                            item = processItem(item);
                            peer = processItem(peer);

                            if (item.section !== peer.section) {
                                // Remove from sectionItems[item.section]
                                sectionItems[item.section].splice(sectionItems[item.section].indexOf(item), 1);
                                sectionWidgets[item.section].domNode.dataset.childCount = sectionItems[item.section].length;

                                // Place before peer in sectionItems[peer.section]
                                sectionItems[peer.section].splice((sectionItems[peer.section].indexOf(peer) + 1), 0, item);
                                sectionWidgets[peer.section].domNode.dataset.childCount = sectionItems[peer.section].length;

                                // Update section tag on item
                                item.section = peer.section;
                            }
                            cmItems.splice(cmItems.indexOf(item), 1);
                            cmItems.splice((cmItems.indexOf(peer) + 1), 0, item);
                        },
                        placeItemIn: function (item, sectionTag) {
                            item = processItem(item);
                            sectionTag = processSectionTag(sectionTag);

                            if (item.section !== sectionTag) {
                                // Remove from sectionItems[item.section]
                                sectionItems[item.section].splice(sectionItems[item.section].indexOf(item), 1);
                                sectionWidgets[item.section].domNode.dataset.childCount = sectionItems[item.section].length;

                                // Place before peer in sectionItems[peer.section]
                                sectionItems[sectionTag].push(item);
                                sectionWidgets[sectionTag].domNode.dataset.childCount = sectionItems[sectionTag].length;

                                // Update section tag on item
                                item.section = sectionTag;

                                // Move the item from its previous location
                                cmItems.splice(cmItems.indexOf(item), 1);
                                var idx = cmItems.indexOf(sectionWidgets[sectionTag]) + sectionItems[sectionTag].length + 1;
                                cmItems.splice(idx, 0, item);
                            }
                        },
                        removeItem: function (item) {
                            var section;

                            item = processItem(item);

                            sectionItems[item.section].splice(sectionItems[item.section].indexOf(item), 1);
                            cmItems.splice(cmItems.indexOf(item), 1);

                            if (sectionItems[item.section].length < 1) {
                                sectionTags[item.section].splice(sectionTags[item.section].indexOf(item.section), 1);
                                delete sectionItems[item.section];
                                section = sectionWidgets[item.section];
                                delete sectionWidgets[item.section];
                                section.destroyRecursive();
                            } else {
                                sectionWidgets[item.section].domNode.dataset.childCount = sectionItems[item.section].length;
                            }

                            delete item.section;
                        },
                        getItems: function (sectionTag) {
                            sectionTag = processSectionTag(sectionTag);

                            return sectionItems[sectionTag];
                        },
                        getItemSection: function (item) {
                            item = processItem(item);

                            return item.section;
                        },
                        hasItem: function (item) {
                            item = processItem(item);

                            return (cmItems.indexOf(item) > -1);
                        },
                        placeSectionBefore: function (sectionTag, peerTag) {
                            sectionTag = processSectionTag(sectionTag);
                            peerTag = processSectionTag(peerTag);

                            // sectionTags
                            sectionTags.splice(sectionTags.indexOf(sectionTag), 1);
                            sectionTags.splice(sectionTags.indexOf(peerTag), 0, sectionTag);

                            // cmItems - Move section Separator
                            cmItems.splice(cmItems.indexOf(sectionWidgets[sectionTag]), 1);
                            var idx = cmItems.indexOf(sectionWidgets[peerTag]);
                            cmItems.splice(idx, 0, sectionWidgets[sectionTag]);

                            // cmItems - Move all child Items
                            var cnt = 0;
                            sectionItems[sectionTag].forEach(function (item) {
                                cmItems.splice(cmItems.indexOf(item), 1);
                                var idx = cmItems.indexOf(sectionWidgets[sectionTag]) + cnt++ + 1;
                                cmItems.splice(idx, 0, item);
                            });
                        },
                        placeSectionAfter: function (sectionTag, peerTag) {
                            sectionTag = processSectionTag(sectionTag);
                            peerTag = processSectionTag(peerTag);

                            // sectionTags
                            sectionTags.splice(sectionTags.indexOf(sectionTag), 1);
                            sectionTags.splice((sectionTags.indexOf(peerTag) + 1), 0, sectionTag);

                            // cmItems - Move section Separator
                            cmItems.splice(cmItems.indexOf(sectionWidgets[sectionTag]), 1);
                            var idx = cmItems.indexOf(sectionWidgets[peerTag]) + sectionItems[peerTag].length + 1;
                            cmItems.splice(idx, 0, sectionWidgets[sectionTag]);

                            // cmItems - Move all child Items
                            var cnt = 0;
                            sectionItems[sectionTag].forEach(function (item) {
                                cmItems.splice(cmItems.indexOf(item), 1);
                                var idx = cmItems.indexOf(sectionWidgets[sectionTag]) + cnt++ + 1;
                                cmItems.splice(idx, 0, item);
                            });
                        },
                        getSections: function () {
                            return sectionTags;
                        },
                        hasSection: function (sectionTag) {
                            sectionTag = processSectionTag(sectionTag);

                            return (sectionTags.indexOf(sectionTag) > -1);
                        }
                    };

                if (selectors.length > 0) {
                    // Call all relevant contributors
                    selectors.forEach(function (selector) {
                        if (matchesSelector(targetNode, selector)) {
                            _private._cmContributors[managerId][selector].forEach(function (callback) {
                                callback(contextMenuBuilder, targetNode);
                            }, this);
                        }
                    }, this);

                    // Add items to context menu
                    if (cmItems.length > 0) {
                        cmItems.forEach(function (item) {
                            _private._contextMenus[managerId].addChild(item); // TODO: Generalize to support multiple CMs
                        }.bind(this));
                    }
                    
                    // Hide the first section separator so that it is still searchable in the DOM, but still conforms to the visual design
                    var contextMenuChildren = _private._contextMenus[managerId].getChildren();
                    if ((contextMenuChildren.length > 0) && contextMenuChildren[0].domNode.classList.contains("mwMenuSection")) {
                        contextMenuChildren[0].domNode.style.display = "none"; // Could be moved to a CSS file
                    }
                }
            }
        };

    return {
        type: "ContextMenuManager",
        version: "1.0.0",
        registerContextMenu: function (contextMenu) {
            if ((contextMenu instanceof ContextMenu) && !_private._isRegistered(contextMenu)) {
                return _private._registerContextMenu(contextMenu);
            }
            return _private._extractManagerId(contextMenu);
        },

        unregisterContextMenu: function (contextMenu) {
            if (((contextMenu instanceof ContextMenu) || (typeof contextMenu === "string")) && _private._isRegistered(contextMenu)) {
                _private._unregisterContextMenu(contextMenu);
            }
        },
        
        isRegistered: function (contextMenu) {return _private._isRegistered(contextMenu);},

        get: function (contextMenu, attr) {
            if (_private._isRegistered(contextMenu)) {
                _private._validateProp(attr);
                return _private["_get" + VALID_PROPERTIES[attr]](contextMenu);
            }
        },

        // TODO: add dependency capability, to allow for callback to be fired after another one
        addContributor: function (contextMenu, selector, callback) {
            if (_private._isRegistered(contextMenu)) {
                if (typeof selector !== "string") {
                    throw new Error("Expected 'selector' input argument to be of type 'string'");
                }

                if (typeof callback !== "function") {
                    throw new Error("Expected 'callback' input argument to be of type 'function'");
                }

                var managerId = _private._extractManagerId(contextMenu);

                if (!_private._cmContributors[managerId].hasOwnProperty(selector)) {
                    _private._cmContributors[managerId][selector] = [];
                }

                if (_private._cmContributors[managerId][selector].indexOf(callback) < 0) {
                    _private._cmContributors[managerId][selector].push(callback);
                }
            }
        },

        removeContributor: function (contextMenu, selector, callback) {
            if (_private._isRegistered(contextMenu)) {
                if (typeof selector !== "string") {
                    throw new Error("Expected 'selector' input argument to be of type 'string'");
                }

                if (typeof callback !== "function") {
                    throw new Error("Expected 'callback' input argument to be of type 'function'");
                }

                var managerId = _private._extractManagerId(contextMenu);

                if (_private._cmContributors[managerId].hasOwnProperty(selector) && (_private._cmContributors[managerId][selector].indexOf(callback) > -1)) {
                    _private._cmContributors[managerId][selector].splice(_private._cmContributors[managerId][selector].indexOf(callback), 1);
                }

                if (_private._cmContributors[managerId][selector].length < 1) {
                    delete _private._cmContributors[managerId][selector];
                }
            }
        },

        build: function (contextMenu, target, sterile) {
            if (_private._isRegistered(contextMenu)) {
                if (sterile) {
                    // Delete all previous children of the Menu and start fresh
                    contextMenu = _private._getContextMenu(contextMenu);
                    contextMenu.getChildren().forEach(function (item) {
                        contextMenu.removeChild(item);
                        item.destroyRecursive();
                    }, this);
                }
                _private._buildContextMenuContributors(contextMenu, target);
            }
        }
    };
});
